/**
 * IFSC Code Verification Utility
 * Uses Razorpay's free public IFSC API
 */

const IFSC_API_BASE_URL = 'https://ifsc.razorpay.com';

/**
 * Verify IFSC code and get bank details
 * @param {string} ifscCode - The IFSC code to verify (11 characters)
 * @returns {Promise<Object>} Bank details or error
 */
export const verifyIFSC = async (ifscCode) => {
    // Validate IFSC format
    if (!ifscCode || typeof ifscCode !== 'string') {
        throw new Error('IFSC code is required');
    }

    // Remove spaces and convert to uppercase
    const cleanIFSC = ifscCode.trim().toUpperCase();

    // Validate IFSC format (11 characters: 4 letters, 7 alphanumeric)
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(cleanIFSC)) {
        throw new Error('Invalid IFSC format. Must be 11 characters (e.g., SBIN0001234)');
    }

    try {
        const response = await fetch(`${IFSC_API_BASE_URL}/${cleanIFSC}`);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('IFSC code not found. Please check and try again.');
            }
            throw new Error('Failed to verify IFSC code. Please try again.');
        }

        const data = await response.json();

        // Return formatted bank details
        return {
            success: true,
            ifsc: data.IFSC,
            bank: data.BANK,
            branch: data.BRANCH,
            address: data.ADDRESS,
            contact: data.CONTACT,
            city: data.CITY,
            district: data.DISTRICT,
            state: data.STATE,
            micr: data.MICR,
            upi: data.UPI,
            rtgs: data.RTGS,
            neft: data.NEFT,
            imps: data.IMPS
        };
    } catch (error) {
        if (error.message.includes('IFSC code not found') || error.message.includes('Invalid IFSC format')) {
            throw error;
        }
        throw new Error('Network error. Please check your connection and try again.');
    }
};

/**
 * Validate IFSC format without API call
 * @param {string} ifscCode - The IFSC code to validate
 * @returns {boolean} True if format is valid
 */
export const isValidIFSCFormat = (ifscCode) => {
    if (!ifscCode || typeof ifscCode !== 'string') {
        return false;
    }

    const cleanIFSC = ifscCode.trim().toUpperCase();
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(cleanIFSC);
};

/**
 * Format IFSC code (uppercase, remove spaces)
 * @param {string} ifscCode - The IFSC code to format
 * @returns {string} Formatted IFSC code
 */
export const formatIFSC = (ifscCode) => {
    if (!ifscCode) return '';
    return ifscCode.trim().toUpperCase().replace(/\s/g, '');
};
